import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AdyenCheckout as createAdyenCheckout,
  ApplePay,
  Card,
  Core,
  GooglePay,
} from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import { toast } from "react-toastify";
import { useCreateAdyenSessionMutation } from "api";
import {
  getAdyenClientKey,
  getAdyenEnvironment,
  getAdyenGooglePayMerchantId,
  getAdyenGooglePayMerchantOrigin,
  getAdyenReturnUrl,
  getLocalAdyenEnvironmentUrls,
} from "helpers/adyenEnv";

export const ADYEN_CARD_CONTAINER_ID = "adyen-payment-container";
export const ADYEN_GOOGLE_PAY_CONTAINER_ID = "adyen-google-pay-button";
export const ADYEN_APPLE_PAY_CONTAINER_ID = "adyen-apple-pay-button";

const toMinorUnits = (total) => Math.max(0, Math.round((total || 0) * 100));

const describeError = (error) => {
  if (typeof error === "object" && error !== null) {
    const cause =
      typeof error.cause === "string"
        ? error.cause
        : typeof error.cause === "object" && error.cause?.message
          ? String(error.cause.message)
          : "";
    const message = typeof error.message === "string" ? error.message : "";

    if (
      message.includes("/sessions/") &&
      message.includes("/setup") &&
      (message.includes("expected pattern") ||
        message.toLowerCase().includes("failed to fetch") ||
        message.toLowerCase().includes("unexpected end"))
    ) {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "this site";
      return `Adyen blocked checkout on ${origin}. In the Adyen Customer Area, open Developers → API credentials → Allowed origins and add that exact origin.`;
    }

    if (
      message.includes("ApplePayService") ||
      cause.toLowerCase().includes("could not get apple pay session") ||
      cause.toLowerCase().includes("could not decode apple pay session")
    ) {
      return "Apple Pay could not verify this website. In Adyen Customer Area, add this exact host under Payment methods → Apple Pay → domains.";
    }

    if (message) {
      return message;
    }
  }

  return "Unable to initialize Adyen payment.";
};

const isIgnoredAdyenError = (error) => {
  if (typeof error === "object" && error !== null && error.name === "CANCEL") {
    return true;
  }
  const message = describeError(error).toLowerCase();
  return (
    message.includes("dismissed") || message.includes("applepay ui dismissed")
  );
};

const isTransientConfigureError = (message) => {
  const normalized = (message || "").toLowerCase();
  return (
    normalized.includes("secured fields have failed to configure") ||
    normalized.includes("iframe has not configured")
  );
};

const getPaymentMethods = (checkout) => {
  const response = checkout?.paymentMethodsResponse;
  if (Array.isArray(response?.paymentMethods)) {
    return response.paymentMethods;
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(checkout?.paymentMethods)) {
    return checkout.paymentMethods;
  }
  return [];
};

const getPaymentMethodConfiguration = (checkout, type) => {
  const aliases =
    type === "googlepay" ? ["googlepay", "paywithgoogle"] : [type];
  return getPaymentMethods(checkout).find((method) =>
    aliases.includes(method?.type),
  )?.configuration;
};

const buildFallbackWalletConfiguration = (type, merchantAccount) => {
  if (type === "googlepay") {
    const merchantId = getAdyenGooglePayMerchantId();
    return {
      ...(merchantAccount ? { gatewayMerchantId: merchantAccount } : {}),
      ...(merchantId ? { merchantId } : {}),
      merchantName: merchantAccount || "Orders.co",
      ...(getAdyenGooglePayMerchantOrigin()
        ? { merchantOrigin: getAdyenGooglePayMerchantOrigin() }
        : {}),
    };
  }
  if (type === "applepay") {
    return {
      merchantName: merchantAccount || "Orders.co",
    };
  }
  return undefined;
};

const ensureWalletPaymentMethods = (checkout, session) => {
  const existing = getPaymentMethods(checkout);
  if (existing.length) {
    return existing;
  }

  const merchantAccount = session?.merchantAccount || "";
  const fallback = [
    { type: "scheme" },
    {
      type: "googlepay",
      name: "Google Pay",
      configuration: buildFallbackWalletConfiguration(
        "googlepay",
        merchantAccount,
      ),
    },
    {
      type: "applepay",
      name: "Apple Pay",
      configuration: buildFallbackWalletConfiguration(
        "applepay",
        merchantAccount,
      ),
    },
  ];

  if (checkout) {
    if (!checkout.paymentMethodsResponse) {
      checkout.paymentMethodsResponse = { paymentMethods: fallback };
    } else {
      checkout.paymentMethodsResponse.paymentMethods = fallback;
    }
  }

  return fallback;
};

const withTimeout = (promise, timeoutMs, label) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`${label} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);

const isWalletAvailable = async (component, label) => {
  const available = await withTimeout(component.isAvailable(), 2000, label);
  if (available === false) {
    throw new Error(`${label} is not available on this device/browser.`);
  }
  return true;
};

const canAttemptApplePay = () => {
  try {
    if (typeof window === "undefined" || !window.ApplePaySession) {
      return false;
    }
    if (window.location.protocol !== "https:") {
      return false;
    }
    return Boolean(window.ApplePaySession.canMakePayments?.());
  } catch {
    return false;
  }
};

const isOfficialWalletButtonStyled = (official) => {
  if (!official.classList.contains("gpay-button")) {
    return true;
  }

  const style = window.getComputedStyle(official);
  const backgroundImage = style.backgroundImage || "";
  return (
    backgroundImage.includes("url(") ||
    Boolean(official.querySelector("img, svg"))
  );
};

const hasVisibleAdyenWalletButton = (containerId) => {
  if (typeof document === "undefined") {
    return false;
  }
  const container = document.getElementById(containerId);
  if (!container) {
    return false;
  }
  const hiddenApple = container.querySelector(
    "apple-pay-button[hidden], apple-pay-button[disabled]",
  );
  const official = container.querySelector(
    ".gpay-button, .adyen-checkout__googlepay, .adyen-checkout__applepay, apple-pay-button:not([hidden])",
  );
  if (!official) {
    return false;
  }
  if (hiddenApple && official === hiddenApple) {
    return false;
  }
  return isOfficialWalletButtonStyled(official);
};

const waitForVisibleWalletButton = async (containerId, timeoutMs = 4000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (hasVisibleAdyenWalletButton(containerId)) {
      return true;
    }
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
  return hasVisibleAdyenWalletButton(containerId);
};

const isLocalHostPage = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const isLocalCheckoutUrl = (value) => {
  const url = String(value || "");
  return (
    url.includes("/checkoutshopper/") &&
    (url.includes("localhost") || url.includes("127.0.0.1"))
  );
};

const AdyenCheckout = async (configuration) => {
  if (!isLocalHostPage() || !configuration?._environmentUrls?.api) {
    return createAdyenCheckout(configuration);
  }

  const originalIncludes = String.prototype.includes;
  // Live client keys require loadingContext to contain "live"; CSF requires
  // `{origin}/checkoutshopper/`. Localhost cannot satisfy both, so skip that check.
  // eslint-disable-next-line no-extend-native
  String.prototype.includes = function patchedIncludes(search, ...rest) {
    if ((search === "live" || search === "test") && isLocalCheckoutUrl(this)) {
      return true;
    }
    return originalIncludes.call(this, search, ...rest);
  };

  try {
    const checkout = new Core(configuration);
    return checkout.initialize();
  } finally {
    // eslint-disable-next-line no-extend-native
    String.prototype.includes = originalIncludes;
  }
};

const resolveClientKey = (session) => getAdyenClientKey(session?.clientKey);

const resolveEnvironment = (session) =>
  getAdyenEnvironment(session?.environment);

const waitForElement = (id, timeoutMs = 3000) => {
  if (typeof document === "undefined") {
    return Promise.resolve(null);
  }

  const existing = document.getElementById(id);
  if (existing) {
    return Promise.resolve(existing);
  }

  const startedAt = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const element = document.getElementById(id);
      if (element) {
        resolve(element);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
};

const clearContainer = (id) => {
  if (typeof document === "undefined") {
    return;
  }
  const container = document.getElementById(id);
  if (container) {
    container.innerHTML = "";
  }
};

const ADYEN_SESSION_STORAGE_PREFIX = "adyen-checkout-session:";
const ADYEN_REDIRECT_PARAM_NAMES = [
  "redirectResult",
  "sessionId",
  "sessionResult",
  "payload",
];

const getRedirectParameters = () => {
  if (typeof window === "undefined") {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  return {
    sessionId: params.get("sessionId") || "",
    redirectResult: params.get("redirectResult") || "",
  };
};

const storeAdyenSession = (session, orderId) => {
  if (typeof window === "undefined" || !session?.id || !session?.sessionData) {
    return;
  }
  sessionStorage.setItem(
    `${ADYEN_SESSION_STORAGE_PREFIX}${session.id}`,
    JSON.stringify({ ...session, orderId }),
  );
};

const readStoredAdyenSession = (sessionId, orderId) => {
  if (typeof window === "undefined" || !sessionId) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(
      `${ADYEN_SESSION_STORAGE_PREFIX}${sessionId}`,
    );
    const session = raw ? JSON.parse(raw) : null;
    return session?.id === sessionId && session?.orderId === orderId
      ? session
      : null;
  } catch {
    return null;
  }
};

const clearStoredAdyenSession = (sessionId) => {
  if (typeof window !== "undefined" && sessionId) {
    sessionStorage.removeItem(`${ADYEN_SESSION_STORAGE_PREFIX}${sessionId}`);
  }
};

const clearAdyenRedirectParameters = () => {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  ADYEN_REDIRECT_PARAM_NAMES.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState({}, document.title, url.toString());
};

const pendingAdyenSessions = new Map();

const getApiErrorMessage = (
  errorData,
  fallback = "Failed to create Adyen session",
) => {
  const apiMessage = errorData?.message || errorData?.error?.message;
  if (Array.isArray(apiMessage)) {
    return apiMessage.join(", ") || fallback;
  }
  return apiMessage || fallback;
};

const requestAdyenSession = (createAdyenSession, body, sessionKey) => {
  const existing = pendingAdyenSessions.get(sessionKey);
  if (existing) {
    return existing;
  }

  const request = createAdyenSession(body).finally(() => {
    pendingAdyenSessions.delete(sessionKey);
  });
  pendingAdyenSessions.set(sessionKey, request);
  return request;
};

const useAdyenPayment = ({
  businessId,
  orderId,
  total,
  tipInfo,
  googlePayEnabled = true,
  applePayEnabled = true,
  canMountWallets = false,
  onPaymentCompleted,
}) => {
  const [createAdyenSession] = useCreateAdyenSessionMutation();
  const [loadingAdyenSession, setLoadingAdyenSession] = useState(false);
  const [adyenStatus, setAdyenStatus] = useState("");
  const [adyenSession, setAdyenSession] = useState(null);
  const [adyenSessionResult, setAdyenSessionResult] = useState("");
  const [adyenPaymentCompleted, setAdyenPaymentCompleted] = useState(false);
  const [adyenCardReady, setAdyenCardReady] = useState(false);
  const [adyenGooglePayReady, setAdyenGooglePayReady] = useState(false);
  const [adyenApplePayReady, setAdyenApplePayReady] = useState(false);
  const [adyenGooglePayLoading, setAdyenGooglePayLoading] = useState(false);
  const [adyenApplePayLoading, setAdyenApplePayLoading] = useState(false);
  const [adyenSubmitting, setAdyenSubmitting] = useState(false);

  const cardRef = useRef(null);
  const googlePayRef = useRef(null);
  const applePayRef = useRef(null);
  const checkoutRef = useRef(null);
  const mountedSessionIdRef = useRef(null);
  const lastSessionKeyRef = useRef("");
  const walletMountKeyRef = useRef("");
  const sessionRef = useRef(null);
  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  const reportedErrorRef = useRef("");

  onPaymentCompletedRef.current = onPaymentCompleted;

  const reportError = useCallback((error) => {
    const message = describeError(error);
    if (!message || reportedErrorRef.current === message) {
      return message;
    }
    reportedErrorRef.current = message;
    toast.error(message);
    return message;
  }, []);

  const minorUnits = useMemo(() => toMinorUnits(total), [total]);

  const isAdyenActive = Boolean(businessId && orderId && minorUnits > 0);

  const unmountComponent = useCallback((component) => {
    try {
      component?.unmount?.();
    } catch {
      // ignore cleanup errors
    }
  }, []);

  const clearWallets = useCallback(() => {
    unmountComponent(googlePayRef.current);
    unmountComponent(applePayRef.current);
    googlePayRef.current = null;
    applePayRef.current = null;
    walletMountKeyRef.current = "";
    clearContainer(ADYEN_GOOGLE_PAY_CONTAINER_ID);
    clearContainer(ADYEN_APPLE_PAY_CONTAINER_ID);
    setAdyenGooglePayReady(false);
    setAdyenApplePayReady(false);
    setAdyenGooglePayLoading(false);
    setAdyenApplePayLoading(false);
  }, [unmountComponent]);

  const clearCheckout = useCallback(() => {
    unmountComponent(cardRef.current);
    cardRef.current = null;
    checkoutRef.current = null;
    mountedSessionIdRef.current = null;
    clearContainer(ADYEN_CARD_CONTAINER_ID);
    clearWallets();
  }, [clearWallets, unmountComponent]);

  const mountWallets = useCallback(
    async (checkout, session = sessionRef.current) => {
      const mountKey = `${mountedSessionIdRef.current || ""}:${googlePayEnabled}:${applePayEnabled}`;
      if (!canMountWallets || walletMountKeyRef.current === mountKey) {
        return;
      }

      if (googlePayEnabled) {
        setAdyenGooglePayLoading(true);
        setAdyenGooglePayReady(false);
      }
      if (applePayEnabled && canAttemptApplePay()) {
        setAdyenApplePayLoading(true);
        setAdyenApplePayReady(false);
      }

      const [googlePayContainer, applePayContainer] = await Promise.all([
        googlePayEnabled
          ? waitForElement(ADYEN_GOOGLE_PAY_CONTAINER_ID, 5000)
          : Promise.resolve(null),
        applePayEnabled && canAttemptApplePay()
          ? waitForElement(ADYEN_APPLE_PAY_CONTAINER_ID, 5000)
          : Promise.resolve(null),
      ]);

      if (!googlePayContainer && !applePayContainer) {
        setAdyenGooglePayLoading(false);
        setAdyenApplePayLoading(false);
        return;
      }

      walletMountKeyRef.current = mountKey;
      ensureWalletPaymentMethods(checkout, session);
      const merchantAccount = session?.merchantAccount || "";

      const mountGooglePay = async () => {
        if (!googlePayEnabled || !googlePayContainer) {
          setAdyenGooglePayReady(false);
          setAdyenGooglePayLoading(false);
          return;
        }

        try {
          const googlePayConfiguration = {
            ...buildFallbackWalletConfiguration("googlepay", merchantAccount),
            ...getPaymentMethodConfiguration(checkout, "googlepay"),
          };
          if (!googlePayConfiguration.merchantId) {
            const merchantId = getAdyenGooglePayMerchantId();
            if (merchantId) {
              googlePayConfiguration.merchantId = merchantId;
            }
          }
          const googlePay = new GooglePay(checkout, {
            buttonType: "pay",
            buttonSizeMode: "fill",
            buttonColor: "black",
            amount: {
              currency: "USD",
              value: minorUnits,
            },
            ...(googlePayConfiguration
              ? { configuration: googlePayConfiguration }
              : {}),
          });
          googlePayRef.current = googlePay;
          try {
            await isWalletAvailable(googlePay, "Google Pay");
          } catch (availabilityError) {
            console.warn(
              "Adyen Google Pay isAvailable failed; mounting the official button anyway.",
              availabilityError,
            );
          }
          const mounted = googlePay.mount(`#${ADYEN_GOOGLE_PAY_CONTAINER_ID}`);
          if (mounted && typeof mounted.then === "function") {
            await mounted;
          }
          const visible = await waitForVisibleWalletButton(
            ADYEN_GOOGLE_PAY_CONTAINER_ID,
          );
          setAdyenGooglePayReady(
            visible ||
              Boolean(
                document
                  .getElementById(ADYEN_GOOGLE_PAY_CONTAINER_ID)
                  ?.querySelector(".gpay-button"),
              ),
          );
        } catch (error) {
          console.warn(
            "Adyen Google Pay is not available on this device/browser.",
            error,
          );
          setAdyenGooglePayReady(false);
        } finally {
          setAdyenGooglePayLoading(false);
        }
      };

      const mountApplePay = async () => {
        if (!applePayEnabled || !applePayContainer) {
          setAdyenApplePayReady(false);
          setAdyenApplePayLoading(false);
          return;
        }

        setAdyenApplePayLoading(true);
        setAdyenApplePayReady(false);
        try {
          const applePayConfiguration = {
            ...buildFallbackWalletConfiguration("applepay", merchantAccount),
            ...getPaymentMethodConfiguration(checkout, "applepay"),
          };
          const applePay = new ApplePay(checkout, {
            buttonType: "plain",
            buttonColor: "black",
            amount: {
              currency: "USD",
              value: minorUnits,
            },
            ...(applePayConfiguration
              ? { configuration: applePayConfiguration }
              : {}),
          });
          applePayRef.current = applePay;
          try {
            await isWalletAvailable(applePay, "Apple Pay");
          } catch (availabilityError) {
            console.warn(
              "Adyen Apple Pay isAvailable failed; mounting the official button anyway.",
              availabilityError,
            );
          }
          const mounted = applePay.mount(`#${ADYEN_APPLE_PAY_CONTAINER_ID}`);
          if (mounted && typeof mounted.then === "function") {
            await mounted;
          }
          const visible = await waitForVisibleWalletButton(
            ADYEN_APPLE_PAY_CONTAINER_ID,
          );
          setAdyenApplePayReady(visible);
        } catch (error) {
          console.warn(
            "Adyen Apple Pay is not available on this device/browser.",
            error,
          );
          setAdyenApplePayReady(false);
        } finally {
          setAdyenApplePayLoading(false);
        }
      };

      await Promise.all([mountGooglePay(), mountApplePay()]);
    },
    [applePayEnabled, canMountWallets, googlePayEnabled, minorUnits],
  );

  const mountCheckout = useCallback(
    async (session, redirectResult = "") => {
      const clientKey = resolveClientKey(session);
      if (!clientKey) {
        setAdyenCardReady(false);
        setAdyenStatus("Adyen session is missing a client key.");
        toast.error(
          "Adyen session did not include a client key. Confirm billing is pointing at the deployed Adyen platform.",
        );
        return;
      }

      if (
        typeof window !== "undefined" &&
        window.isSecureContext === false &&
        window.location.hostname !== "localhost" &&
        window.location.hostname !== "127.0.0.1"
      ) {
        console.warn(
          "Adyen Secured Fields need HTTPS (or localhost). Card iframes may fail on this origin.",
        );
      }

      clearCheckout();
      sessionRef.current = session;
      const environment = resolveEnvironment(session);
      const localUrls = getLocalAdyenEnvironmentUrls(environment);
      let checkoutReady = false;
      try {
        const checkout = await AdyenCheckout({
          clientKey,
          environment,
          ...(localUrls ? { _environmentUrls: localUrls } : {}),
          countryCode: "US",
          locale: "en-US",
          amount: {
            currency: "USD",
            value: minorUnits,
          },
          paymentMethodsConfiguration: {
            googlepay: {
              buttonType: "pay",
              buttonSizeMode: "fill",
              buttonColor: "black",
            },
            applepay: {
              buttonType: "plain",
              buttonColor: "black",
            },
          },
          session: {
            id: session.id,
            sessionData: session.sessionData,
          },
          onPaymentCompleted: (result) => {
            const sessionResult = result?.sessionResult || "";
            setAdyenSubmitting(false);
            setAdyenSessionResult(sessionResult);
            setAdyenPaymentCompleted(true);
            setAdyenStatus("Payment completed. Finalizing your order...");
            Promise.resolve(
              onPaymentCompletedRef.current?.({
                sessionId: session.id,
                sessionResult,
                resultCode: result?.resultCode,
              }),
            )
              .then((completed) => {
                if (completed === true) {
                  clearStoredAdyenSession(session.id);
                  clearAdyenRedirectParameters();
                }
              })
              .catch((error) => {
                console.error("Unable to finalize Adyen payment:", error);
              });
          },
          onPaymentFailed: (result) => {
            clearStoredAdyenSession(session.id);
            clearAdyenRedirectParameters();
            setAdyenSubmitting(false);
            setAdyenPaymentCompleted(false);
            setAdyenSessionResult("");
            const message = `Payment failed: ${result?.resultCode || "failed"}.`;
            setAdyenStatus(message);
            toast.error(message);
          },
          onError: (error) => {
            if (isIgnoredAdyenError(error)) {
              setAdyenSubmitting(false);
              return;
            }
            const message = describeError(error);
            if (isTransientConfigureError(message)) {
              return;
            }
            setAdyenSubmitting(false);
            setAdyenPaymentCompleted(false);
            setAdyenSessionResult("");
            setAdyenStatus("Adyen payment form reported an error.");
            if (checkoutReady) {
              toast.error(message);
            }
          },
        });
        checkoutReady = true;

        checkoutRef.current = checkout;
        mountedSessionIdRef.current = session.id;

        if (redirectResult) {
          setAdyenSubmitting(true);
          setAdyenStatus("Confirming your redirected Adyen payment...");
          await checkout.submitDetails({
            details: { redirectResult },
          });
          return;
        }

        const card = new Card(checkout, {
          showPayButton: false,
        });
        const cardContainer = await waitForElement(ADYEN_CARD_CONTAINER_ID);
        if (cardContainer) {
          card.mount(`#${ADYEN_CARD_CONTAINER_ID}`);
          cardRef.current = card;
          setAdyenCardReady(true);
          setAdyenStatus(
            "Enter your card details below to complete the payment.",
          );
        } else {
          setAdyenCardReady(false);
        }

        if (canMountWallets) {
          await mountWallets(checkout, session);
        }
      } catch (error) {
        setAdyenCardReady(false);
        setAdyenStatus("Unable to mount the Adyen payment form.");
        console.error("Adyen Checkout failed to mount", error);
        reportError(error);
      }
    },
    [canMountWallets, clearCheckout, minorUnits, mountWallets, reportError],
  );

  useEffect(() => {
    if (!isAdyenActive) {
      clearCheckout();
      setAdyenSession(null);
      setAdyenSessionResult("");
      setAdyenPaymentCompleted(false);
      setAdyenCardReady(false);
      setAdyenStatus("");
      lastSessionKeyRef.current = "";
      return;
    }

    const sessionKey = `${businessId}:${orderId}:${minorUnits}:${JSON.stringify(
      tipInfo || {},
    )}`;
    if (lastSessionKeyRef.current === sessionKey) {
      return;
    }

    let cancelled = false;
    const createSession = async () => {
      try {
        setLoadingAdyenSession(true);
        setAdyenCardReady(false);
        setAdyenPaymentCompleted(false);
        setAdyenSessionResult("");
        setAdyenSubmitting(false);
        reportedErrorRef.current = "";
        setAdyenStatus("Preparing secure Adyen payment form...");

        const redirect = getRedirectParameters();
        if (redirect.sessionId && redirect.redirectResult) {
          const storedSession = readStoredAdyenSession(
            redirect.sessionId,
            orderId,
          );
          if (!storedSession) {
            throw new Error(
              "The redirected Adyen session is unavailable. Check the order status before trying another payment.",
            );
          }
          lastSessionKeyRef.current = sessionKey;
          setAdyenSession(storedSession);
          await mountCheckout(storedSession, redirect.redirectResult);
          return;
        }

        const returnUrl = getAdyenReturnUrl();
        const response = await requestAdyenSession(
          createAdyenSession,
          {
            businessId,
            orderId,
            tipInfo,
            returnUrl,
            countryCode: "US",
            capture: true,
          },
          sessionKey,
        );

        if (cancelled) {
          return;
        }

        if (response?.error) {
          throw new Error(getApiErrorMessage(response.error?.data));
        }

        const payload = response?.data;
        const nextSession =
          payload?.id && payload?.sessionData
            ? payload
            : payload?.data?.id && payload?.data?.sessionData
              ? payload.data
              : null;

        if (!nextSession) {
          throw new Error("Adyen session response is missing required fields.");
        }

        lastSessionKeyRef.current = sessionKey;
        storeAdyenSession(nextSession, orderId);
        setAdyenSession(nextSession);
        await mountCheckout(nextSession);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("Unable to create Adyen session:", error);
        clearCheckout();
        setAdyenSession(null);
        setAdyenStatus("Unable to load Adyen payment form.");
        // API failures are already toasted by rtkQueryErrorLogger.
      } finally {
        if (!cancelled) {
          setLoadingAdyenSession(false);
        }
      }
    };

    void createSession();

    return () => {
      cancelled = true;
    };
  }, [
    businessId,
    clearCheckout,
    createAdyenSession,
    isAdyenActive,
    minorUnits,
    mountCheckout,
    orderId,
    tipInfo,
  ]);

  useEffect(() => {
    if (!canMountWallets || !checkoutRef.current) {
      return;
    }
    void mountWallets(checkoutRef.current, sessionRef.current);
  }, [canMountWallets, mountWallets]);

  useEffect(() => clearCheckout, [clearCheckout]);

  const submitAdyenPayment = useCallback(() => {
    if (!cardRef.current?.submit) {
      toast.error("Adyen payment form is not ready yet.");
      return false;
    }

    setAdyenSubmitting(true);
    cardRef.current.submit();
    return true;
  }, []);

  const submitAdyenGooglePay = useCallback(() => {
    if (!googlePayRef.current?.submit) {
      toast.error("Google Pay is not ready yet.");
      return false;
    }
    setAdyenSubmitting(true);
    googlePayRef.current.submit();
    return true;
  }, []);

  const submitAdyenApplePay = useCallback(() => {
    if (!applePayRef.current?.submit) {
      toast.error("Apple Pay is not ready yet.");
      return false;
    }
    setAdyenSubmitting(true);
    applePayRef.current.submit();
    return true;
  }, []);

  return {
    adyenCardReady,
    adyenPaymentCompleted,
    adyenSession,
    adyenSessionResult,
    adyenStatus,
    adyenGooglePayReady,
    adyenApplePayReady,
    adyenGooglePayLoading,
    adyenApplePayLoading,
    adyenSubmitting,
    loadingAdyenSession,
    dropinContainerId: ADYEN_CARD_CONTAINER_ID,
    googlePayContainerId: ADYEN_GOOGLE_PAY_CONTAINER_ID,
    applePayContainerId: ADYEN_APPLE_PAY_CONTAINER_ID,
    submitAdyenPayment,
    submitAdyenGooglePay,
    submitAdyenApplePay,
  };
};

export default useAdyenPayment;
